import useSWR from 'swr';
import { fetcher } from '@shared/fetcher';
import { ProductSchema, IProduct } from '@shared/types.ts';
import { ProductCardProps } from '@ui/product/product-card.tsx';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '@uidotdev/usehooks';
import { useUser } from '../authorization/use-user.ts';
import { useCartProductsStore } from '../cart/use-cart-products.store.ts';
import { useAtomValue } from 'jotai/index';
import { cityAtom } from '@app/atoms/app/city.atom.ts';

async function products(url: string) {
  const res = await fetcher.get<{ data: IProduct[] }>(url).catch(() => null);
  if (!res?.data) return undefined;
  return res?.data?.data?.filter((i) => ProductSchema.isValidSync(i));
}

export function useProducts({
  categoryIds,
  query,
  searchParams,
}: {
  categoryIds?: number[];
  query?: string;
  searchParams?: URLSearchParams;
}) {
  const city = useAtomValue(cityAtom);
  const params = searchParams ? `&${searchParams.toString()}` : '';
  const categories: string[] = [];
  categoryIds?.forEach((id, index) => {
    categories.push(`categories[${index}]=${id}`);
  });
  const navigate = useNavigate();
  const queryDebounce = useDebounce(query ? `&search=${query}` : '', 800);
  const { data, isLoading } = useSWR(
    `/api/products?${categories.length > 0 ? categories.join('&') : ''}${queryDebounce}&city_ids[0]=${city.id}${params}`,
    products
  );

  const { isUseCache } = useUser();

  const { isProductInCart, insertProduct } = useCartProductsStore({
    isUseCache,
  });

  const mapped = data?.map<ProductCardProps | undefined>((product) => {
    const mappedProduct: ProductCardProps = {
      image: product.main_photo || undefined,
      title: product.name,
      price: product.price || '',
      isShowPrice: true,
      isSelected: isProductInCart(product.id),
      description: product.category_name || '',
      onPress: () => navigate(`/products/${product.id}`),
      onPressAction: () => insertProduct(product),
    };
    return mappedProduct;
  });

  const array = Array(10)
    .fill(undefined)
    .map(() => ({
      isLoading: true,
    }));

  return {
    data: mapped || array,
    isLoading,
    rawData: data,
  };
}
